// components/ui/Collapsible.tsx
import { ChevronRight } from 'lucide-react'
import React, { useRef, useState } from 'react'
import { cn } from '~/@/lib/utils'

interface CollapsibleProps {
  title: React.ReactNode
  children: React.ReactNode
  defaultOpen?: boolean
  titleClassName?: string
  lazy?: boolean // Don't render children until first open
  keepMounted?: boolean // Keep children mounted after first render
}

export function Collapsible({
  title,
  children,
  defaultOpen = false,
  titleClassName = '',
  lazy = true, // Don't render children until first open
  keepMounted = true, // Keep state when collapsed
}: CollapsibleProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [hasBeenOpened, setHasBeenOpened] = useState(defaultOpen)
  const contentRef = useRef<HTMLDivElement>(null)

  const handleTitleRowClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Title rows often embed checkboxes (role form, table permissions). Those clicks must not toggle
    // the collapsible or the section appears to randomly re-collapse and scroll jumps.
    const target = e.target as HTMLElement
    if (
      target.closest(
        'button, [role="checkbox"], [role="switch"], input, select, textarea, label, a[href], .doss-checkbox',
      )
    ) {
      return
    }
    setIsOpen((prev) => {
      const newState = !prev
      if (newState && !hasBeenOpened) {
        setHasBeenOpened(true)
      }
      return newState
    })
  }

  // Determine whether to render children
  const shouldRenderChildren = () => {
    if (!lazy) {
      return true
    }
    if (keepMounted && hasBeenOpened) {
      return true
    }
    return isOpen
  }

  return (
    <div>
      <div
        className={cn(
          'flex items-center cursor-pointer hover:bg-gray-1 transition-colors duration-150',
          titleClassName,
        )}
        onClick={handleTitleRowClick}
      >
        <ChevronRight
          className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-90 transform' : ''
          }`}
        />
        <div className="min-w-0 flex-1">{title}</div>
      </div>
      <div
        id="collapsible-content"
        ref={contentRef}
        className="overflow-hidden transition-all duration-200 ease-in-out"
      >
        {shouldRenderChildren() && (
          <div className={cn(!isOpen && keepMounted && 'sr-only')}>{children}</div>
        )}
      </div>
    </div>
  )
}
